import _ from "lodash";
import moment from "moment";

import {
  getIsConfigurationEnabledForCPAY,
  getSettingValue,
} from "modules/PermissionModule";
import { getCurriculumLevelsQuery } from "./GradingSetupV2Queries";
import { getLevelsQueryVariables } from "SchoolSetup/modules/Utils";
// import { store } from "store/createStore";
// import { getCurriculumProgramId } from "Login/routes/Administrator/routes/SchoolSetup/routes/Students/modules/StudentsModule";
import {
  getAvatarData,
  getShouldShowCourseDisplayName,
  getIsBlueprintCourse,
  getIsCourseBasedGradingEnabled,
} from "LearningCourses/modules/Utils";
import {
  USE_WEIGHTAGE_FOR_CATEGORY_SETTING,
  OVERALL_SCORE_FOR_GRADING_SETUP_SETTING,
  CATEGORY_FINAL_SCORE_DISPLAY_TYPE_SETTING,
  TERM_FINAL_SCORE_DISPLAY_TYPE,
  GRADE_CALCULATION_METHOD_SETTING,
  LOCAL_GRADE_CALCULATION_BASED_ON_SETTING,
  MYP_CRITERIA_TOTAL_CALCULATION_BASED_ON_SETTING,
  DECIMAL_POINTS_FOR_FINAL_SCORE_SETTING,
  FINAL_GRADE_CALCULATION_SETTING,
} from "GradingSetup/modules/Utils";
import {
  SCORE_BASED,
  STANDARD_BASED,
  MYP_CRITERIA_BASED,
  CUSTOM_WEIGHTS,
  SCORE,
  GRADE_SCALE,
  STAFF,
  CATEGORY_GRADE_SCALE,
  COURSE_BASED_GRADING,
  CURRICULUM_TYPE_DP,
  STUDENT,
} from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import {
  ALWAYS_VISIBLE,
  GRADING_METHODOLOGY_TYPES,
  HIDDEN,
  VISIBLE_FOR_PAST_GRADING_PERIODS,
} from "./Constants";
import { generateRandomId, getInitials } from "Utils";
import {
  getCourseGradingConfigQueryFromCache,
  getMultiSubjectsGradingConfigQueryFromCache,
} from "./GradingSetupV2GraphqlHelper";
import {
  GRADING_VISIBILITY_SETTINGS_V2_PERM,
  USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
} from "Constants/permissionConstants";
import { CURRICULUM_PROGRAM_TO_DATASET_GROUPS_MAP } from "../../Datasets/modules/constants";
import { getCurrentDatasetsGroupType } from "../../Datasets/modules/Utils";
import {
  getCurrentCurriculumProgramType,
  getCurrentPlannerElementSets,
} from "modules/CommonSelectors";

const styles = {
  gradingTypeColumnStyles: {
    align: "center",
  },
};

export const getIsGpaCreditsV2Enabled = () => {
  return ACLStore.can("FeatureFlag:EnableGpaCreditsV2");
};

export const getIsAbbreviationLengthLimited = () => {
  return !ACLStore.can("FeatureFlag:RemoveAbbreviationLimit");
};

export const getUseDataGridGradeScales = () => {
  return ACLStore.can("FeatureFlag:UseDataGridGradeScales");
};

export const shouldShowLevelsInGradingSetupV2 = _curriculumProgramId => {
  // uncomment when useLevelsInSchoolValue config is available

  // let curriculumProgramId = _curriculumProgramId;
  // if (!curriculumProgramId) {
  //   curriculumProgramId = getCurriculumProgramId(store.getState());
  // }

  // const useLevelsInSchool = getSettingValue({
  //   name: "useLevelsInSchoolValue", // use correct value
  //   currentCurriculumProgramId: curriculumProgramId,
  // });

  const featureFlagValue = ACLStore.can(
    "FeatureFlag:ShowLevelsInGradingSetupV2"
  );

  // return useLevelsInSchool && featureFlagValue
  return featureFlagValue;
};

export const isGradingVisibilitySettingsV2Enabled = ({
  academicYearId,
  curriculumProgramId,
}) => {
  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    academicYearId,
    curriculumProgramId,
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
  });

  return (
    isUsingNewGradingSetupInCourses &&
    ACLStore.can(GRADING_VISIBILITY_SETTINGS_V2_PERM)
  );
};

export const getLevelsQueryConfig = ({
  curriculumProgramId,
  curriculumNodeInterfaceType,
}) => {
  return {
    query: getCurriculumLevelsQuery,
    skipCondition: _.isNil(curriculumNodeInterfaceType),
    variables: getLevelsQueryVariables({
      curriculumProgramId,
      curriculumType: curriculumNodeInterfaceType,
    }),
    parseData: data => _.get(data, "node.levels", []),
  };
};

export const getIsJsonstringifyUid = uid => {
  return _.includes(
    [
      "selectedLevels",
      "gradeScalesForAggregation",
      "isPerTermGradingTypeMap",
      "decimalSettings",
    ],
    uid
  );
};

export const normalizeSettingValue = (value, isJSONStringify = false) => {
  if (_.isString(value)) {
    if (value === "true") return true;
    if (value === "false") return false;
    if (!isNaN(value) && value.trim() !== "") return Number(value);
    if (isJSONStringify) {
      return JSON.parse(value);
    }
  }
  return value;
};

export const getGradingSetupConfigurations = ({
  scoreGradingConfig,
  curriculumProgramId,
  isUsingNewGradingSetupInCourses,
}) => {
  if (isUsingNewGradingSetupInCourses) {
    const settings = _.get(scoreGradingConfig, "settings", []);
    const settingsMap = _.reduce(
      settings,
      (acc, setting) => {
        const normalizedValue = normalizeSettingValue(
          setting.value,
          getIsJsonstringifyUid(setting.uid)
        );
        _.set(acc, [setting.groupType, setting.uid], normalizedValue);
        return acc;
      },
      {}
    );

    const calculationMethod = _.get(settingsMap, [
      "REPORTING",
      "assignmentContributionMethod",
    ]);
    const categoryTotalDisplayType = _.get(settingsMap, [
      "REPORTING",
      "scoreDisplayFormat",
    ]);
    const cumulativeAssociatedTermTotalDisplayType = _.get(settingsMap, [
      "CUMULATIVE",
      "scoreDisplayFormat",
    ]);
    const isCategoriesEnabled = _.get(settingsMap, [
      "CATEGORY",
      "enableCategories",
    ]);
    const isWeightageForCategoryEnabled = _.get(settingsMap, [
      "CATEGORY",
      "isCategoryWeighted",
    ]);
    const differentCategoriesForGradingPeriods = _.get(settingsMap, [
      "CATEGORY",
      "differentCategoriesForGradingPeriods",
    ]);
    const enableCategoryGrade = _.get(settingsMap, [
      "CATEGORY",
      "enableCategoryGrade",
    ]);
    const cumulativeTotalCalculationMethod = _.get(settingsMap, [
      "CUMULATIVE",
      "cumulativeTotalCalculationMethod",
    ]);
    const differentWeightsForLevels = _.get(settingsMap, [
      "CATEGORY",
      "differentWeightsForLevels",
    ]);
    const decimalPointsForFinalScore = _.get(settingsMap, [
      "REPORTING",
      "decimalPointsSetting",
    ]);
    const cumulativeDecimalPointsForFinalScore = _.get(settingsMap, [
      "CUMULATIVE",
      "decimalPointsSetting",
    ]);
    const selectedLevelsForCategory = _.get(
      settingsMap,
      ["CATEGORY", "selectedLevels"],
      []
    );

    return {
      enableCategoryGrade,
      isOverallScoreEnabled: true,
      categoryTotalDisplayType,
      cumulativeAssociatedTermTotalDisplayType,
      calculationMethod,
      isWeightageForCategoryEnabled,
      decimalPointsForFinalScore,
      cumulativeTotalCalculationMethod,
      cumulativeDecimalPointsForFinalScore,
      differentCategoriesForGradingPeriods,
      differentWeightsForLevels,
      isCategoriesEnabled,
      selectedLevelsForCategory,
    };
  } else {
    const calculationMethod = getSettingValue({
      name: GRADE_CALCULATION_METHOD_SETTING,
      currentCurriculumProgramId: curriculumProgramId,
    });

    const categoryTotalDisplayType = getSettingValue({
      name: CATEGORY_FINAL_SCORE_DISPLAY_TYPE_SETTING,
      currentCurriculumProgramId: curriculumProgramId,
    });

    const cumulativeAssociatedTermTotalDisplayType = getSettingValue({
      name: TERM_FINAL_SCORE_DISPLAY_TYPE,
      currentCurriculumProgramId: curriculumProgramId,
    });

    const isWeightageForCategoryEnabled = getSettingValue({
      name: USE_WEIGHTAGE_FOR_CATEGORY_SETTING,
      currentCurriculumProgramId: curriculumProgramId,
    });

    const localGradeCalculationMethod = getSettingValue({
      name: LOCAL_GRADE_CALCULATION_BASED_ON_SETTING,
      currentCurriculumProgramId: curriculumProgramId,
    });

    const decimalPointsForFinalScore = getSettingValue({
      name: DECIMAL_POINTS_FOR_FINAL_SCORE_SETTING,
      currentCurriculumProgramId: curriculumProgramId,
    });

    const mypCriteriaTotalCalculationMethod = getSettingValue({
      name: MYP_CRITERIA_TOTAL_CALCULATION_BASED_ON_SETTING,
      currentCurriculumProgramId: curriculumProgramId,
    });

    const isOverallScoreEnabled = getSettingValue({
      name: OVERALL_SCORE_FOR_GRADING_SETUP_SETTING,
      currentCurriculumProgramId: curriculumProgramId,
    });

    const finalGradeCalculationMethod = getSettingValue({
      name: FINAL_GRADE_CALCULATION_SETTING,
      currentCurriculumProgramId: curriculumProgramId,
    });

    return {
      isOverallScoreEnabled,
      categoryTotalDisplayType,
      cumulativeAssociatedTermTotalDisplayType,
      calculationMethod,
      isWeightageForCategoryEnabled,
      finalGradeCalculationMethod,
      localGradeCalculationMethod,
      decimalPointsForFinalScore,
      cumulativeDecimalPointsForFinalScore: decimalPointsForFinalScore,
      mypCriteriaTotalCalculationMethod,
    };
  }
};

export const getTaggableGlobalCategories = ({ globalCategories, data }) => {
  const taggedGlobalCategories = _.map(data, item =>
    _.get(item, "globalCategory.id")
  );

  return _.filter(
    globalCategories,
    ({ node }) => !_.includes(taggedGlobalCategories, node?.id)
  );
};

export const getDefaultCategorySetRowData = ({
  globalCategories,
  differentWeightsForLevels,
  levels,
}) => {
  const rowId = generateRandomId();

  const globalCategory = _.get(globalCategories, "0.node");

  return {
    id: rowId,
    globalCategory,
    valueDetails: [
      {
        weight: "",
        subjectLevelId: null,
      },
      ...(differentWeightsForLevels
        ? _.map(levels, level => ({
            subjectLevelId: level.id,
            weight: "",
          }))
        : []),
    ],
  };
};

export const getScoreGradingConfigInitialState = ({
  levels,
  globalCategories,
  scoreGradingConfig,
  reportingGradingPeriods,
  cumulativeGradingPeriods,
  SCORE_CALCULATION_CONFIGURATION,
}) => {
  const settings = _.get(scoreGradingConfig, "settings", []);
  const categorySets = _.get(scoreGradingConfig, "gradingCategorySets", []);
  const cumulativeTermWeights = _.get(
    scoreGradingConfig,
    "cumulativeTermWeights",
    []
  );

  const initialState = {
    CATEGORY: _.reduce(
      SCORE_CALCULATION_CONFIGURATION.CATEGORY,
      (acc, item) => {
        if (item.uid) {
          acc[item.uid] = item.defaultValue;
        }
        return acc;
      },
      {
        categories: [],
        gradingPeriodWiseSetup: [],
      }
    ),
    REPORTING: _.reduce(
      SCORE_CALCULATION_CONFIGURATION.REPORTING,
      (acc, item) => {
        if (item.uid) {
          if (item.uid === "overallScoreSettings") {
            acc.decimalTypeSetting = null;
            acc.decimalPointsSetting = null;
          } else {
            acc[item.uid] = null;
          }
        }
        return acc;
      },
      {}
    ),
    CUMULATIVE: _.reduce(
      SCORE_CALCULATION_CONFIGURATION.CUMULATIVE,
      (acc, item) => {
        if (item.uid) {
          if (item.uid === "overallScoreSettings") {
            acc.decimalTypeSetting = null;
            acc.decimalPointsSetting = null;
          } else {
            acc[item.uid] = null;
          }
        }
        return acc;
      },
      {
        cumulativeTermWeights: [],
      }
    ),
  };

  const groupedSettings = _.groupBy(settings, "groupType");

  _.forEach(["REPORTING", "CUMULATIVE", "CATEGORY"], groupType => {
    _.forEach(_.get(groupedSettings, groupType, []), setting => {
      const value = normalizeSettingValue(
        setting.value,
        getIsJsonstringifyUid(setting.uid)
      );
      _.set(initialState, `${groupType}.${setting.uid}`, value);
    });
  });

  // Category Settings
  const categoriesFromData = _.get(
    _.find(categorySets, set => _.isEmpty(_.get(set, "academicTerm"))),
    "gradingCategoryValues"
  );

  let categories = [];
  const isCategoryEnabled = normalizeSettingValue(
    _.find(settings, { uid: "enableCategories" })?.value,
    getIsJsonstringifyUid("enableCategories")
  );
  const differentWeightsForLevels = normalizeSettingValue(
    _.find(settings, { uid: "differentWeightsForLevels" })?.value,
    getIsJsonstringifyUid("differentWeightsForLevels")
  );

  if (_.isEmpty(categoriesFromData) && isCategoryEnabled) {
    const taggableGlobalCategories = getTaggableGlobalCategories({
      globalCategories,
      data: [],
    });

    categories = [
      getDefaultCategorySetRowData({
        globalCategories: taggableGlobalCategories,
        differentWeightsForLevels,
        levels,
      }),
    ];
  } else {
    categories = _.map(categoriesFromData, set => {
      return {
        id: _.get(set, "id"),
        globalCategory: {
          id: _.get(set, "globalCategory.id"),
          name: _.get(set, "globalCategory.name"),
        },
        gradeScale: _.get(set, "gradeScale"),
        valueDetails: _.map(_.get(set, "valueDetails"), item => {
          return {
            id: _.get(item, "id"),
            weight: _.toNumber(_.get(item, "weight")),
            subjectLevelId: _.get(item, "subjectLevel.id", null),
          };
        }),
      };
    });
  }

  _.set(initialState, "CATEGORY.categories", categories);

  // Different Categories for Grading Periods
  const termWiseCategorySetsFromData = _.filter(categorySets, set =>
    _.get(set, "academicTerm.id")
  );
  const isDifferentCategoriesForGradingPeriods = normalizeSettingValue(
    _.find(settings, {
      uid: "differentCategoriesForGradingPeriods",
    })?.value,
    getIsJsonstringifyUid("differentCategoriesForGradingPeriods")
  );
  const gradingPeriodWiseSetup = [];

  _.forEach(reportingGradingPeriods, term => {
    const termId = _.get(term, "id");
    let categories = [];
    const termCategories = _.get(
      _.find(
        termWiseCategorySetsFromData,
        set => _.get(set, "academicTerm.id") === termId
      ),
      "gradingCategoryValues"
    );

    if (!_.isEmpty(termCategories)) {
      categories = _.map(termCategories, category => ({
        id: _.get(category, "id"),
        globalCategory: {
          id: _.get(category, "globalCategory.id"),
          name: _.get(category, "globalCategory.name"),
        },
        gradeScale: _.get(category, "gradeScale"),
        valueDetails: _.map(_.get(category, "valueDetails"), item => {
          return {
            id: _.get(item, "id"),
            weight: _.toNumber(_.get(item, "weight")),
            subjectLevelId: _.get(item, "subjectLevel.id", null),
          };
        }),
      }));
    } else if (isDifferentCategoriesForGradingPeriods) {
      const taggableGlobalCategories = getTaggableGlobalCategories({
        globalCategories,
        data: [],
      });
      categories = [
        getDefaultCategorySetRowData({
          globalCategories: taggableGlobalCategories,
          differentWeightsForLevels,
          levels,
        }),
      ];
    }

    if (!_.isEmpty(categories)) {
      gradingPeriodWiseSetup.push({
        termId,
        categories,
      });
    }
  });

  const isGradingPeriodWeighted =
    normalizeSettingValue(
      _.find(settings, {
        uid: "cumulativeTotalCalculationMethod",
      })?.value,
      getIsJsonstringifyUid("cumulativeTotalCalculationMethod")
    ) === "WEIGHTED_GRADING_PERIODS";

  if (isGradingPeriodWeighted) {
    _.set(
      initialState,
      "CUMULATIVE.cumulativeTermWeights",
      _.map(
        cumulativeGradingPeriods,
        ({ id: cumulativeTermId, associatedAcademicTermEntities }) => ({
          cumulativeTermId,
          associatedTermWeights: _.map(
            associatedAcademicTermEntities,
            ({ academicTerm }) => {
              const termId = _.get(academicTerm, "id");

              // weight in state
              const weight = _.find(
                _.get(
                  _.find(cumulativeTermWeights, {
                    cumulativeTermId,
                  }),
                  "associatedTermWeights"
                ),
                { termId }
              )?.weight;

              return {
                termId,
                weight: weight || 0,
              };
            }
          ),
        })
      )
    );
  }

  _.set(
    initialState,
    "CATEGORY.gradingPeriodWiseSetup",
    gradingPeriodWiseSetup
  );

  return initialState;
};

export const checkAllFields = scoreConfigSettings => {
  const requiredFields = {
    CATEGORY: ["enableCategories", "isCategoryWeighted", "categories"],
    REPORTING: [
      "assignmentContributionMethod",
      "scoreDisplayFormat",
      "decimalTypeSetting",
      "decimalPointsSetting",
    ],
    CUMULATIVE: [
      "cumulativeTotalCalculationMethod",
      "decimalTypeSetting",
      "decimalPointsSetting",
    ],
  };

  return _.some(_.entries(requiredFields), ([section, fields]) =>
    _.some(fields, field => {
      if (
        field === "differentCategoriesForGradingPeriods" ||
        (field === "categories" &&
          _.get(scoreConfigSettings, "CATEGORY.enableCategories") === false)
      )
        return false;

      const value = _.get(scoreConfigSettings, `${section}.${field}`);
      return _.includes([null, undefined, "", []], value);
    })
  );
};

export const getFormattedGradingConfigSettingValue = (
  value,
  isJSONStringify = false
) => {
  if (_.isBoolean(value)) {
    return String(value);
  }

  if (isJSONStringify) {
    return JSON.stringify(value);
  }

  if (_.isObject(value)) {
    return _.get(value, "value", null);
  }

  return _.isNil(value) ? null : String(value);
};

export const transformSettings = ({ settings, shouldSkip }) => {
  const transformedSettings = [];

  _.forEach(Object.keys(settings), groupType => {
    const groupSettings = _.get(settings, groupType, {});

    _.forEach(Object.keys(groupSettings), uid => {
      if (shouldSkip && shouldSkip({ uid, groupType })) {
        return;
      }

      const value = getFormattedGradingConfigSettingValue(
        _.get(groupSettings, uid),
        getIsJsonstringifyUid(uid)
      );

      transformedSettings.push({
        uid,
        value,
        groupType,
      });
    });
  });

  return transformedSettings;
};

const transformSettingsToGradingConfig = ({
  scoreConfigSettings,
  initialConfigSettings,
  cumulativeGradingPeriods,
}) => {
  const scoreGradingConfigSettings = transformSettings({
    settings: scoreConfigSettings,
    shouldSkip: ({ uid, groupType }) => {
      switch (groupType) {
        case "CATEGORY": {
          return uid === "categories" || uid === "gradingPeriodWiseSetup";
        }
        case "CUMULATIVE": {
          return uid === "cumulativeTermWeights";
        }
        default: {
          return false;
        }
      }
    },
  });

  const { enableCategories, differentCategoriesForGradingPeriods } =
    scoreConfigSettings?.CATEGORY || {};

  const gradingPeriodWiseSetup = differentCategoriesForGradingPeriods
    ? _.get(scoreConfigSettings, "CATEGORY.gradingPeriodWiseSetup")
    : [];

  const categorySetsLocal = _.concat(
    {
      categories: _.get(scoreConfigSettings, "CATEGORY.categories") || [],
    },
    gradingPeriodWiseSetup || []
  );

  const prevCategorySets = _.concat(
    {
      categories: _.get(initialConfigSettings, "CATEGORY.categories") || [],
    },
    _.get(initialConfigSettings, "CATEGORY.gradingPeriodWiseSetup") || []
  );

  const gradingCategorySets = enableCategories
    ? _.map(categorySetsLocal, ({ termId, categories }) => ({
        termId,
        upsertAndRemoveGradingCategoryValues: _.filter(
          _.map(categories, category => {
            const prevCategory = getPrevCategory({
              termId,
              category,
              prevCategorySets,
            });

            return normalizeCategoryValue(category, prevCategory);
          }),
          ({ globalCategoryId }) => !_.isEmpty(globalCategoryId)
        ),
      }))
    : [];

  const isCumulativePeriodsWeighted =
    _.get(
      scoreConfigSettings,
      "CUMULATIVE.cumulativeTotalCalculationMethod"
    ) === "WEIGHTED_GRADING_PERIODS";

  const cumulativeTermWeights = isCumulativePeriodsWeighted
    ? getAssociatedCumulativeTermWeights({
        cumulativeGradingPeriods,
        cumulativeTermWeightsLocal: _.get(
          scoreConfigSettings,
          "CUMULATIVE.cumulativeTermWeights"
        ),
      })
    : [];

  return {
    scoreGradingConfigSettings,
    gradingCategorySets,
    cumulativeTermWeights,
  };
};

export const getIsSubjectGradebookEnabled = () => {
  return ACLStore.can("FeatureFlag:ShowSameSubjectGradeTasksInGradebook");
};

const normalizeCategoryValue = (value, prevValue) => {
  const normalizedValue = {
    globalCategoryId: _.get(value, "globalCategory.id"),
    valueDetails: _.map(value.valueDetails, detail => ({
      subjectLevelId: detail.subjectLevelId,
      weight: _.toNumber(detail.weight) || 0,
    })),
  };

  const gradeScaleId = _.get(value, "gradeScale.0.id");
  const prevGradeScaleId = _.get(prevValue, "gradeScale.0.id");

  const upsertGradeScaleInput = _.get(value, "upsertGradeScaleInput");
  const prevUpsertGradeScaleInput = _.get(prevValue, "upsertGradeScaleInput");

  let upsertGradeScale = {};

  // handle change or remove grade scale
  if (gradeScaleId !== prevGradeScaleId) {
    if (!_.isEmpty(prevGradeScaleId)) {
      normalizedValue.removeGradeScaleId = prevGradeScaleId;
    }

    if (!_.isEmpty(gradeScaleId)) {
      upsertGradeScale = {
        id: gradeScaleId,
      };
    }
  }

  // handle grade scale values changes
  if (
    !_.isEmpty(upsertGradeScaleInput) &&
    !_.isEqual(prevUpsertGradeScaleInput, upsertGradeScaleInput)
  ) {
    upsertGradeScale = {
      id: gradeScaleId,
      ...upsertGradeScaleInput,
    };
  }

  if (!_.isEmpty(upsertGradeScale)) {
    normalizedValue.upsertGradeScale = upsertGradeScale;
  }

  return normalizedValue;
};

const getPrevCategory = ({ prevCategorySets, termId, category }) => {
  const prevCategories = _.find(
    prevCategorySets,
    prevSet => prevSet?.termId === termId
  )?.categories;

  return _.find(
    prevCategories,
    prevCategory =>
      prevCategory?.globalCategory?.id === category?.globalCategory?.id
  );
};

export const getUpdatedCategorySets = ({
  scoreConfigSettings,
  prevScoreConfigSettings,
}) => {
  const categorySetsLocal = [];
  if (
    _.get(scoreConfigSettings, "CATEGORY.differentCategoriesForGradingPeriods")
  ) {
    categorySetsLocal.push(
      ...(_.get(scoreConfigSettings, "CATEGORY.gradingPeriodWiseSetup") || [])
    );
  } else {
    categorySetsLocal.push({
      categories: _.get(scoreConfigSettings, "CATEGORY.categories") || [],
    });
  }

  const prevCategorySets = _.concat(
    {
      categories: _.get(prevScoreConfigSettings, "CATEGORY.categories") || [],
    },
    _.get(prevScoreConfigSettings, "CATEGORY.gradingPeriodWiseSetup") || []
  );

  const updateGradingCategorySets = [];
  _.forEach(categorySetsLocal, ({ termId, categories }) => {
    updateGradingCategorySets.push({
      termId,
      upsertAndRemoveGradingCategoryValues: _.filter(
        _.map(categories, category => {
          const prevCategory = getPrevCategory({
            termId,
            category,
            prevCategorySets,
          });

          return normalizeCategoryValue(category, prevCategory);
        }),
        ({ globalCategoryId }) => !_.isEmpty(globalCategoryId)
      ),
    });
  });

  return updateGradingCategorySets;
};

export const normalizeTermWeight = ({ termId, weight }) => ({
  termId,
  weight: _.toNumber(weight) || 0,
});

const getAssociatedCumulativeTermWeights = ({
  cumulativeGradingPeriods,
  cumulativeTermWeightsLocal,
}) => {
  const cumulativeTermWeightsLocalMap = _.keyBy(
    cumulativeTermWeightsLocal,
    "cumulativeTermId"
  );

  return _.filter(
    _.map(cumulativeGradingPeriods, ({ id }) => {
      const updatedAssociatedTermWeights = _.get(
        cumulativeTermWeightsLocalMap[id],
        "associatedTermWeights",
        []
      );

      const associatedTermWeights = [];

      _.forEach(updatedAssociatedTermWeights, ({ termId, weight }) => {
        associatedTermWeights.push({
          termId,
          weight,
        });
      });

      if (_.isEmpty(associatedTermWeights)) {
        return null;
      }

      return {
        cumulativeTermId: id,
        associatedTermWeights,
      };
    }),
    Boolean
  );
};

export const getBulkApplyMutationVariables = ({
  scoreConfigSettings,
  curriculumProgramId,
  initialConfigSettings,
  cumulativeGradingPeriods,
  selectedEntities,
  shouldUpdateCurriculumLevelWithOverride,
  academicYearId,
}) => {
  const {
    scoreGradingConfigSettings,
    gradingCategorySets,
    cumulativeTermWeights,
  } = transformSettingsToGradingConfig({
    scoreConfigSettings,
    initialConfigSettings,
    cumulativeGradingPeriods,
  });

  const entityType = shouldUpdateCurriculumLevelWithOverride
    ? "CURRICULUM_PROGRAM"
    : selectedEntities?.entityType;

  const entityDetails = [];

  switch (entityType) {
    case "CURRICULUM_PROGRAM": {
      entityDetails.push({ curriculumProgramId });
      break;
    }
    case "LEARNING_COURSE": {
      const { selectedCourses } = selectedEntities || {};
      _.forEach(selectedCourses, learningCourseId => {
        entityDetails.push({ learningCourseId });
      });
      break;
    }
    case "SUBJECT_GRADE_MAP": {
      const { selectedSubjects } = selectedEntities || {};
      _.forEach(selectedSubjects, ({ id: subjectId, grades }) => {
        _.forEach(grades, gradeId => {
          entityDetails.push({
            subjectId,
            gradeId,
          });
        });
      });
      break;
    }
  }

  return {
    input: {
      overrideChildConfigurations: shouldUpdateCurriculumLevelWithOverride
        ? true
        : undefined,
      entityType,
      entityDetails,
      gradingConfigurationType: SCORE_BASED,
      updateGradingConfigurationSettings: scoreGradingConfigSettings,
      updateGradingCategorySets: gradingCategorySets,
      cumulativeTermWeights,
      academicYearId:
        entityType === "LEARNING_COURSE" ? undefined : academicYearId,
    },
  };
};

const COURSE_LISTING_CONFIGURATION = [
  {
    uid: "COURSE_ID",
    locale_key: "",
    valueType: "label",
    width: 60,
    toggleAllCheckboxesOnHeaderClick: true,
    defaultValue: "",
  },
  {
    uid: "COURSES",
    locale_key: "course:courses",
    valueType: "label",
    width: 250,
    toggleAllCheckboxesOnHeaderClick: false,
    defaultValue: "",
  },
  {
    uid: "CODE",
    locale_key: "schoolSetup:course_code",
    valueType: "label",
    width: 140,
    toggleAllCheckboxesOnHeaderClick: false,
    defaultValue: "",
    isConfigurable: true,
  },
  {
    uid: "COURSE_TYPE",
    locale_key: "course:course_type",
    valueType: "label",
    width: 180,
    toggleAllCheckboxesOnHeaderClick: false,
    defaultValue: "",
    isConfigurable: true,
  },
  {
    uid: "GRADING_METHOD",
    locale_key: "academicSetup:grading_method",
    valueType: "label",
    width: 200,
    toggleAllCheckboxesOnHeaderClick: false,
    defaultValue: "",
  },
  // TODO: Will be added once the data is available
  // {
  //   uid: "associatedBlueprint",
  //   locale_key: "behaviour:updated_by",
  //   valueType: "label",
  //   width: 300,
  //   defaultValue: "",
  // },
  {
    uid: "SUBJECT",
    locale_key: "common:subject_one_or_more",
    valueType: "text",
    width: 180,
    toggleAllCheckboxesOnHeaderClick: false,
    defaultValue: "",
    isConfigurable: true,
  },
  {
    uid: "GRADE",
    locale_key: "academicSetup:grade_one_or_more",
    valueType: "text",
    width: 180,
    toggleAllCheckboxesOnHeaderClick: false,
    defaultValue: "",
    isConfigurable: true,
  },
  {
    uid: "TERM",
    locale_key: "common:grading_period_one_or_more",
    valueType: "text",
    width: 180,
    toggleAllCheckboxesOnHeaderClick: false,
    defaultValue: "",
    isConfigurable: true,
  },
  {
    uid: "APPLICABLE_GRADING_PERIODS",
    locale_key: "academicSetup:applicable_grading_periods",
    valueType: "dropdown",
    width: 360,
    toggleAllCheckboxesOnHeaderClick: true,
  },
  {
    uid: "TOTAL_CREDITS",
    locale_key: "common:total_credits",
    valueType: "label",
    width: 120,
    toggleAllCheckboxesOnHeaderClick: false,
    defaultValue: "",
    isConfigurable: true,
  },
  {
    uid: "ADDED_VALUE",
    locale_key: "academicSetup:added_value",
    valueType: "number",
    width: 120,
    toggleAllCheckboxesOnHeaderClick: false,
    defaultValue: "0",
    isConfigurable: true,
  },
  {
    uid: SCORE_BASED,
    locale_key: "common:score_based_grading",
    valueType: "checkbox",
    width: 200,
    toggleAllCheckboxesOnHeaderClick: true,
    defaultValue: true,
    styles: styles.gradingTypeColumnStyles,
  },
  {
    uid: STANDARD_BASED,
    locale_key: "common:standard_based_grading",
    valueType: "checkbox",
    width: 200,
    toggleAllCheckboxesOnHeaderClick: true,
    defaultValue: true,
    styles: styles.gradingTypeColumnStyles,
  },
  {
    uid: MYP_CRITERIA_BASED,
    locale_key: "academicSetup:myp_criteria_based_grading",
    valueType: "checkbox",
    width: 200,
    toggleAllCheckboxesOnHeaderClick: true,
    defaultValue: true,
    styles: styles.gradingTypeColumnStyles,
  },
  {
    uid: "ACTIONS",
    columnKey: "ACTIONS",
    locale_key: "common:actions",
    width: 80,
    styles: styles.gradingTypeColumnStyles,
  },
];

export const getCourseListingHeaders = ({
  t,
  customColumns,
  containerWidth,
  hideColumnSelector,
  configureColumnsData,
}) => {
  const DATA_GRID_DEFAULT_WIDTH = 800;

  const filteredFields = _.filter(COURSE_LISTING_CONFIGURATION, ({ uid }) => {
    let condition = _.includes(customColumns, uid);

    if (!hideColumnSelector) {
      condition =
        condition && _.get(configureColumnsData, [uid, "isSelected"], true);
    }

    return condition;
  });

  const headers = [];
  _.forEach(filteredFields, item => {
    const { uid, locale_key, styles, width, valueType, isConfigurable } = item;

    const isCheckbox = uid === "COURSE_ID";

    const headerItem = {
      id: uid,
      columnKey: uid,
      title: t(locale_key),
      width: width,
      styles,
      valueType,
      isConfigurable,
    };

    if (isCheckbox) {
      headerItem.toggleAllCheckboxesOnHeaderClick = true;
    }

    headers.push(headerItem);
  });

  return getHeadersWithScaledWidths({
    headers,
    containerWidth: containerWidth - 4, // 4 for padding
    parentDefaultWidth: DATA_GRID_DEFAULT_WIDTH,
  });
};

export const getHeadersWithScaledWidths = ({
  headers,
  containerWidth,
  parentDefaultWidth,
  isMultiLevelHeaders,
}) => {
  const getScaleFactor = (parentWidth, totalPreferred) => {
    return parentWidth / totalPreferred;
  };

  const calculateScaledWidth = ({ scaleFactor, width, minWidth, maxWidth }) => {
    const scaledWidth = Math.floor(width * scaleFactor) - 2; // subtracting 2 for border width

    return _.chain(scaledWidth)
      .thru(w => (_.isNil(minWidth) || w >= minWidth ? w : minWidth))
      .thru(w => (_.isNil(maxWidth) || w <= maxWidth ? w : maxWidth))
      .value();
  };

  const parentWidth = containerWidth || parentDefaultWidth;

  if (isMultiLevelHeaders) {
    const leafHeaders = _.filter(_.flatMap(headers), { isLeaf: true });
    const totalPreferred = _.sum(_.map(leafHeaders, "width"));
    const scaleFactor = getScaleFactor(parentWidth, totalPreferred);

    _.forEach(headers, row => {
      _.forEach(row, header => {
        const { width, minWidth, maxWidth, isLeaf } = header || {};

        if (!isLeaf || !header) return;

        header.width = calculateScaledWidth({
          scaleFactor,
          width,
          minWidth,
          maxWidth,
        });
      });
    });
    return headers;
  } else {
    const preferredWidths = _.map(headers, "width");
    const minWidths = _.map(headers, "minWidth");
    const maxWidths = _.map(headers, "maxWidth");
    const totalPreferred = _.sum(preferredWidths);
    const scaleFactor = getScaleFactor(parentWidth, totalPreferred);

    const scaledWidths = _.map(preferredWidths, (width, index) =>
      calculateScaledWidth({
        scaleFactor,
        width,
        minWidth: minWidths[index],
        maxWidth: maxWidths[index],
      })
    );

    // Calculate remaining space to redistribute
    const currentTotal = _.sum(scaledWidths);
    const remainingSpace = parentWidth - currentTotal;

    // Find expandable columns and redistribute space
    const redistributeSpace = widths => {
      if (remainingSpace <= 0) return widths;

      const expandableIndices = _.chain(widths)
        .map((width, index) => {
          const maxWidth = maxWidths[index];
          return _.isNil(maxWidth) || width < maxWidth ? index : null;
        })
        .filter(index => !_.isNil(index)) // filter out nulls but not 0, compact removes 0
        .value();

      if (_.isEmpty(expandableIndices)) return widths;

      const spacePerColumn = Math.floor(
        remainingSpace / expandableIndices.length
      );

      return _.map(widths, (width, index) => {
        if (!_.includes(expandableIndices, index)) return width;

        const maxWidth = maxWidths[index];
        const newWidth = width + spacePerColumn - 2;

        return _.isNil(maxWidth) || newWidth <= maxWidth ? newWidth : maxWidth;
      });
    };

    const finalWidths = redistributeSpace(scaledWidths);

    return _.map(headers, (item, index) => ({
      ...item,
      width: finalWidths[index] || item.width,
    }));
  }
};

export const getFlattenedSubjectGrades = ({ subjectGradesData, filters }) => {
  const subjectIds = _.get(filters, "subjects", []);
  const gradeIds = _.get(filters, "grades", []);
  const shouldFilterSubjects = !_.isEmpty(subjectIds);
  const shouldFilterGrades = !_.isEmpty(gradeIds);

  return _.flatMap(subjectGradesData, subjectItem => {
    const subjectId = _.get(subjectItem, "id");
    if (shouldFilterSubjects && !_.includes(subjectIds, subjectId)) return [];
    const gradingMethodologies = _.get(subjectItem, "gradingMethodologies", []);
    const filteredGrades = _.filter(_.get(subjectItem, "grades"), grade => {
      return !shouldFilterGrades || _.includes(gradeIds, _.get(grade, "id"));
    });
    return _.map(filteredGrades, grade => {
      const gradeId = _.get(grade, "id");
      const matchedGradingMethodology = _.find(
        gradingMethodologies,
        gm => _.get(gm, "entityDetail.gradeId") === gradeId
      );

      return {
        gradeId,
        gradeName: _.get(grade, "name"),
        subjectId,
        subjectName: _.get(subjectItem, "name"),
        gradingMethodologyTypes: _.get(
          matchedGradingMethodology,
          "gradingMethodologyTypes",
          []
        ),
      };
    });
  });
};

export const getSubjectGradesTableData = ({
  subjectGradesData,
  gradingMethodologyTypes,
  tableHeaders,
  isEditMode,
  filters,
}) => {
  const flattenedSubjectGrades = getFlattenedSubjectGrades({
    subjectGradesData,
    filters,
  });
  return _.map(
    flattenedSubjectGrades,
    ({ gradeId: rowId, gradeName, subjectId, subjectName }) => {
      const columnData = _.map(tableHeaders, column => {
        const { id, ...restProps } = column;
        switch (id) {
          case "SUBJECT":
            return {
              ...restProps,
              columnId: id,
              value: subjectName,
            };
          case "GRADE":
            return {
              ...restProps,
              columnId: id,
              value: gradeName,
            };
          case SCORE_BASED:
          case STANDARD_BASED:
          case MYP_CRITERIA_BASED: {
            const isChecked = _.includes(
              gradingMethodologyTypes?.[`${subjectId}-${rowId}`],
              id
            );

            let value = "";
            if (isEditMode) {
              value = isChecked ? "checked" : "unchecked";
            } else {
              value = isChecked ? "TickCircleFilled" : "CloseCircleOutlined";
            }

            return {
              columnId: id,
              subjectGradeId: `${subjectId}-${rowId}`,
              cellType: isEditMode ? "checkbox" : "icon",
              isEditable: isEditMode,
              isDisabled: false,
              value,
            };
          }
          default:
            break;
        }
      });

      return {
        rowId,
        columns: columnData,
      };
    }
  );
};

export const getSubjectGradesTableHeaders = ({
  t,
  selectedGradingMethodology,
  containerWidth,
}) => {
  const DATA_GRID_DEFAULT_WIDTH = 800;
  const baseProps = {
    cellType: "label",
    isDisabled: false,
    isEditable: false,
  };

  const baseHeaders = [
    {
      id: "SUBJECT",
      columnKey: "SUBJECT",
      title: t("common:subject"),
      width: 180,
      ...baseProps,
    },
    {
      id: "GRADE",
      columnKey: "GRADE",
      title: t("common:grade"),
      width: 180,
      ...baseProps,
    },
  ];

  const gradingMethodologyHeaders = [
    {
      id: SCORE_BASED,
      columnKey: SCORE_BASED,
      title: t("common:score_based_grading"),
      width: 200,
      styles: styles.gradingTypeColumnStyles,
      ...baseProps,
    },
    {
      id: STANDARD_BASED,
      columnKey: STANDARD_BASED,
      title: t("common:standard_based_grading"),
      width: 200,
      styles: styles.gradingTypeColumnStyles,
      ...baseProps,
    },
    {
      id: MYP_CRITERIA_BASED,
      columnKey: MYP_CRITERIA_BASED,
      title: t("academicSetup:myp_criteria_based_grading"),
      width: 200,
      styles: styles.gradingTypeColumnStyles,
      ...baseProps,
    },
  ];

  const filteredGradingMethodologyHeaders = _.filter(
    gradingMethodologyHeaders,
    ({ columnKey }) => {
      return _.includes(selectedGradingMethodology, columnKey);
    }
  );

  return getHeadersWithScaledWidths({
    headers: _.concat(baseHeaders, filteredGradingMethodologyHeaders),
    containerWidth,
    parentDefaultWidth: DATA_GRID_DEFAULT_WIDTH,
  });
};

const getIsAllItemsSelectedByKey = ({
  key,
  gradingPeriods = [],
  updatedFilters,
  organizationGrades,
  organizationSubjects,
}) => {
  const allOptionsKeyMap = {
    subjects: _.get(organizationSubjects, "subjects", []),
    grades: _.get(organizationGrades, "grades", []),
    academicTerms: gradingPeriods,
    types: ["BLUEPRINT_COURSE", "TEACHER_COURSE"],
  };
  const allOptions = _.get(allOptionsKeyMap, key);

  if (!allOptions) {
    return false;
  }

  const selectedValues = _.get(updatedFilters, key, []);

  return _.size(selectedValues) === _.size(allOptions);
};

export const getLearningCourseFilters = ({
  filters = {},
  organizationGrades,
  organizationSubjects,
  gradingPeriods,
}) => {
  const updatedFilters = { ...filters };

  _.forEach(Object.keys(filters), key => {
    const isAllItemsSelected = getIsAllItemsSelectedByKey({
      key,
      gradingPeriods,
      updatedFilters,
      organizationGrades,
      organizationSubjects,
    });

    if (isAllItemsSelected) {
      updatedFilters[key] = [];
    }
  });

  return updatedFilters;
};

export const getIsDefaultLearningCourseFilters = filters => {
  const { searchText, subjects, grades, academicTerms, types } = filters || {};

  return (
    _.isEmpty(searchText) &&
    _.isEmpty(subjects) &&
    _.isEmpty(grades) &&
    _.isEmpty(academicTerms) &&
    _.isEmpty(types)
  );
};

export const getGradingMethodologyOptions = params => {
  const {
    t,
    isGradeScale,
    curriculumType,
    checkIsGradingMethodologyDisabled = () => false,
  } = params || {};

  return _.reduce(
    GRADING_METHODOLOGY_TYPES,
    (result, item) => {
      const { uid, locale_header, locale_key, checkIsHidden } = item;

      if (checkIsHidden && checkIsHidden({ isGradeScale, curriculumType })) {
        return result;
      }

      if (!checkIsGradingMethodologyDisabled(item)) {
        result.push({
          value: uid,
          label: t(locale_header),
          subtitle: t(locale_key),
          isDisabled: checkIsGradingMethodologyDisabled(item),
        });
      }

      return result;
    },
    []
  );
};

export const transformVisibilitySettingsToLocal = ({ visibilitySettings }) => {
  const initialState = {};
  _.forEach(visibilitySettings, setting => {
    const { groupType, uid, value } = setting || {};
    let parsedSettingValue = null;

    try {
      parsedSettingValue = JSON.parse(value);
    } catch (e) {
      console.error(e);
    }

    _.setWith(initialState, `${groupType}.${uid}`, parsedSettingValue, Object);
  });

  return initialState;
};

export const transformVisibilitySettingsToLocalV2 = ({
  visibilitySettings,
  userType,
}) => {
  try {
    const initialState = {};

    _.forEach(visibilitySettings, setting => {
      const { groupType, uid, value } = setting || {};
      const parsedSettingValue = JSON.parse(value);

      // In v2, we use a different structure with nested groups
      const [mainGroup, subGroup, key] = uid.split(".");

      if (subGroup && key) {
        // Handle nested structure
        _.setWith(
          initialState,
          `${groupType}.${mainGroup}.${subGroup}.${key}`,
          parsedSettingValue,
          Object
        );
      } else {
        _.setWith(
          initialState,
          `${groupType}.${mainGroup}`,
          parsedSettingValue,
          Object
        );
      }
    });

    if (userType) {
      const userVisibilityType =
        userType === STUDENT ? "STUDENT_VISIBILITY" : "PARENT_VISIBILITY";
      return _.get(initialState, userVisibilityType, {});
    }

    return initialState;
  } catch {
    return {};
  }
};

export const getBulkUpdateVisibilitySettingsInput = ({
  visibilitySettingsLocal,
  visibilitySettingsConfigId,
  entityType,
  entityDetails,
  academicYearId,
}) => {
  const updateGradingConfigurationSettings = [];

  _.forEach(visibilitySettingsLocal, (settings, groupType) => {
    _.forEach(settings, (value, settingUID) => {
      updateGradingConfigurationSettings.push({
        groupType,
        uid: settingUID,
        value: JSON.stringify(value),
      });
    });
  });

  return {
    input: {
      gradingConfigurationIds: visibilitySettingsConfigId
        ? [visibilitySettingsConfigId]
        : undefined,
      gradingConfigurationType: "GRADING_VISIBILITY_SETTINGS",
      entityType,
      entityDetails,
      updateGradingConfigurationSettings,
      academicYearId:
        entityType === "LEARNING_COURSE" ? undefined : academicYearId,
    },
  };
};

const VISIBILITY_GROUP_TYPES = ["STUDENT_VISIBILITY", "PARENT_VISIBILITY"];
const VISIBILITY_ENTITY_GROUPS = [
  "FINAL_GRADES_SETTINGS",
  "ASSESSMENT_GRADES_SETTINGS",
  "STANDARDS_GRADES_SETTINGS",
  "LEARNING_GOALS_GRADES_SETTINGS",
];

export const getBulkUpdateVisibilitySettingsInputV2 = ({
  visibilitySettingsLocal,
  visibilitySettings,
  visibilitySettingsConfigId,
  entityType,
  entityDetails,
  academicYearId,
}) => {
  const updateGradingConfigurationSettings = [];

  // For each combination of group type and entity group, check if we need to update
  _.forEach(VISIBILITY_GROUP_TYPES, groupType => {
    _.forEach(VISIBILITY_ENTITY_GROUPS, entityGroup => {
      const newValue = _.get(
        visibilitySettingsLocal,
        `${groupType}.${entityGroup}`
      );

      if (!_.isNil(newValue)) {
        // For existing settings
        if (visibilitySettingsConfigId) {
          const existingSetting = _.find(visibilitySettings, {
            groupType,
            uid: entityGroup,
          });

          const existingValue = existingSetting
            ? JSON.parse(existingSetting.value)
            : null;

          // Only update if values are different
          if (!_.isEqual(existingValue, newValue)) {
            updateGradingConfigurationSettings.push({
              id: existingSetting?.id,
              groupType,
              uid: entityGroup,
              value: JSON.stringify(newValue),
            });
          }
        } else {
          // For new settings
          updateGradingConfigurationSettings.push({
            groupType,
            uid: entityGroup,
            value: JSON.stringify(newValue),
          });
        }
      }
    });
  });

  return {
    input: {
      gradingConfigurationIds: visibilitySettingsConfigId
        ? [visibilitySettingsConfigId]
        : undefined,
      gradingConfigurationType: "GRADING_VISIBILITY_SETTINGS",
      entityType,
      entityDetails,
      updateGradingConfigurationSettings,
      academicYearId:
        entityType === "LEARNING_COURSE" ? undefined : academicYearId,
    },
  };
};

const filterTypes = {
  SUBJECT_GRADE_FILTERS: ["subjects", "grades"],
  COURSE_LIST_FILTERS: ["types", "grades", "academicTerms", "subjects"],
  COURSEWISE_CONFIG_FILTERS: [
    "types",
    "grades",
    "academicTerms",
    "subjects",
    "states",
  ],
  COURSE_LIST_FILTERS_WITHOUT_TERMS: ["types", "grades", "subjects"],
  SUBJECT_LIST_FILTERS_WITHOUT_TERMS: ["subjects", "grades"],
};

const getStateAndCourseTypeOptions = ({ t }) => {
  const courseTypeOptions = [
    {
      key: "BLUEPRINT_COURSE",
      label: t("academicSetup:blueprint_course"),
      value: "BLUEPRINT_COURSE",
    },
    {
      key: "TEACHER_COURSE",
      label: t("academicSetup:teacher_course"),
      value: "TEACHER_COURSE",
    },
  ];

  const stateOptions = [
    {
      key: "ACTIVE",
      label: t("common:active"),
      value: "ACTIVE",
    },
    {
      key: "ARCHIVED",
      label: t("common:archived"),
      value: "ARCHIVED",
    },
  ];
  return { courseTypeOptions, stateOptions };
};

export const getFilterData = ({
  t,
  filters,
  gradingPeriods,
  organizationGrades,
  organizationSubjects,
  filterType,
}) => {
  const filterKeys = filterTypes[filterType] || [];

  const filterData = {};

  const { stateOptions, courseTypeOptions } = getStateAndCourseTypeOptions({
    t,
  });

  _.forEach(filterKeys, key => {
    switch (key) {
      case "types":
        {
          filterData[key] = {
            label: t("course:course_type"),
            options: courseTypeOptions,
            selectedValues: _.get(filters, "types", []),
          };
        }

        break;
      case "grades":
        {
          const gradeOptions = _.map(
            _.get(organizationGrades, "grades"),
            grade => {
              return {
                key: _.get(grade, "id"),
                label: _.get(grade, "name"),
                value: _.get(grade, "id"),
              };
            }
          );

          filterData[key] = {
            label: t("common:grades"),
            options: gradeOptions,

            selectedValues: _.get(filters, "grades", []),
          };
        }

        break;
      case "subjects":
        {
          const subjectOptions = _.map(
            _.get(organizationSubjects, "subjects"),
            subject => {
              return {
                key: _.get(subject, "id"),
                label: _.get(subject, "name"),
                value: _.get(subject, "id"),
              };
            }
          );

          filterData[key] = {
            label: t("common:subject_plural"),
            options: subjectOptions,
            selectedValues: _.get(filters, "subjects", []),
          };
        }

        break;
      case "states":
        {
          filterData[key] = {
            label: t("academicSetup:states"),
            options: stateOptions,
            selectedValues: _.get(filters, "states", []),
          };
        }

        break;
      case "academicTerms": {
        const gradingPeriodOptions = _.map(gradingPeriods, term => {
          return {
            id: _.get(term, "id"),
            key: _.get(term, "id"),
            label: _.get(term, "label"),
            value: _.get(term, "id"),
          };
        });

        filterData[key] = {
          label: t("common:grading_period_plural"),
          options: gradingPeriodOptions,
          selectedValues: _.get(filters, "academicTerms", []),
        };
      }
    }
  });

  return filterData;
};

const getText = (items, key) =>
  _.map(items, item => _.get(item, key)).join(", ");

export const getGradingPeriodOptionsForCourse = ({
  learningCourse,
  gradingPeriodOptions,
}) => {
  const learningCourseGradingPeriodIds = _.map(
    _.get(learningCourse, "gradingPeriods.edges"),
    "node.id"
  );

  return _.filter(gradingPeriodOptions, ({ id }) => {
    return _.includes(learningCourseGradingPeriodIds, id);
  });
};

export const getCourseListingTableData = ({
  t,
  tableHeaders,
  learningCoursesData,
  gradingPeriodOptions,
  isEditMode,
  gradingMethodologyTypes = {},
  selectedCourses,
  selectedGradingPeriods,
  courseAddedValues = {},
  onClickDelete,
  creditsData = [],
}) => {
  const gradingMethodologyTypesTextMap = {
    STANDARD_BASED: t("academicSetup:standard_based"),
    SCORE_BASED: t("academicSetup:score_based"),
    MYP_CRITERIA_BASED: t("academicSetup:myp_criteria_based_grading"),
  };

  const courseType = {
    BLUEPRINT_COURSE: t("academicSetup:blueprint_course"),
    TEACHER_COURSE: t("academicSetup:teacher_course"),
  };

  const hasSeparateColForCode = _.some(tableHeaders, {
    id: "CODE",
  });

  return _.map(learningCoursesData, item => {
    const node = _.has(item, "node") ? _.get(item, "node") : item;

    const {
      id,
      title,
      academicCourse,
      code,
      type,
      grades = [],
      gradingMethodology = {},
      subjects = [],
      displayMetadata,
    } = node;

    const displayName = _.get(academicCourse, "label");

    const courseDisplayName =
      getShouldShowCourseDisplayName() && displayName ? displayName : title;

    const learningCourseGradingPeriods = getGradingPeriodOptionsForCourse({
      learningCourse: node,
      gradingPeriodOptions,
    });

    const columns = _.map(tableHeaders, header => {
      const { id: colId, valueType } = header;
      const commonProps = {
        columnId: colId,
        cellType: "label",
        isDisabled: false,
        isEditable: false,
      };
      const commonAvatarProps = {
        type: "avatar",
        avatarId: id,
        theme: "light",
        shape: "square",
      };

      const isCourseSelected = _.includes(selectedCourses, id);

      switch (colId) {
        case "COURSE_ID":
          return {
            columnId: colId,
            cellType: "checkbox",
            isEditable: isEditMode,
            isDisabled: false,
            value: isCourseSelected ? "checked" : "unchecked",
          };
        case "COURSES": {
          const { color, name, iconType } = getAvatarData({
            id,
            title: courseDisplayName,
            profileImageData: displayMetadata,
          });

          const icon = _.get(displayMetadata, "icon");

          return {
            ...commonProps,
            value: courseDisplayName,
            subtitle: hasSeparateColForCode ? undefined : code,
            prefix: [
              {
                ...commonAvatarProps,
                size: 28,
                name: getInitials({ value: name }),
                color,
                icon,
                iconType,
              },
            ],
            cellType: valueType,
          };
        }
        case "CODE":
          return { ...commonProps, value: code, cellType: valueType };
        case "COURSE_TYPE":
          return {
            ...commonProps,
            value: courseType?.[type] || "",
          };
        case "GRADING_METHOD": {
          const gradingMethodologyText = _.join(
            _.map(
              _.get(gradingMethodology, "gradingMethodologyTypes", []),
              type => gradingMethodologyTypesTextMap[type]
            ),
            ", "
          );

          return {
            ...commonProps,
            value: gradingMethodologyText,
            cellType: valueType,
          };
        }
        case "SUBJECT": {
          const subjectsText = getText(subjects, "name");

          return {
            ...commonProps,
            value: subjectsText,
          };
        }
        case "GRADE": {
          const gradesText = getText(grades, "name");

          return { ...commonProps, value: gradesText };
        }
        case "TERM": {
          const termsText = getText(learningCourseGradingPeriods, "label");

          return { ...commonProps, value: termsText };
        }
        case SCORE_BASED:
        case STANDARD_BASED:
        case MYP_CRITERIA_BASED: {
          const isBlueprintCourse = getIsBlueprintCourse(node);
          const pageTypes = _.get(node, "lockingInfo.pageTypes");
          const isGradingConfigLocked =
            !isBlueprintCourse &&
            _.includes(pageTypes, "GRADING_CONFIGURATION");

          const isChecked = _.includes(gradingMethodologyTypes?.[id], colId);
          let value = "";

          if (isEditMode) {
            value = isChecked ? "checked" : "unchecked";
          } else {
            value = isChecked ? "TickCircleFilled" : "CloseCircleOutlined";
          }

          return {
            columnId: colId,
            cellType: isEditMode ? "checkbox" : "icon",
            isEditable: isEditMode,
            isDisabled: isEditMode && isGradingConfigLocked,
            value,
          };
        }
        case "APPLICABLE_GRADING_PERIODS":
          return {
            columnId: colId,
            value: selectedGradingPeriods?.[id] || [],
            cellType: valueType,
            isMulti: true,
            addNewOptionEnabled: false,
            isEditable: true,
            validInputs: {
              options: learningCourseGradingPeriods,
            },
            placeholder: "Select",
          };
        case "TOTAL_CREDITS": {
          // Find the credit entry for this course where academicTerm is null (total credits)
          const totalCreditEntry = _.find(
            creditsData,
            credit =>
              _.get(credit, "learningCourse.id") === id &&
              _.isNil(_.get(credit, "academicTerm"))
          );
          const totalCredits = _.get(totalCreditEntry, "credit");
          return {
            ...commonProps,
            value: !_.isNil(totalCredits) ? _.toString(totalCredits) : "-",
            cellType: "label",
            isDisabled: true,
          };
        }
        case "ADDED_VALUE":
          return {
            columnId: colId,
            value: courseAddedValues?.[id] || "0",
            cellType: "number",
            isEditable: isEditMode,
            isDisabled: false,
          };
        case "ACTIONS":
          return {
            cellType: "icon",
            value: "DeleteOutlined",
            isEditable: true,
            isDisabled: false,
            onIconClick: ({ rowId }) => onClickDelete(rowId),
          };
        default:
          return null;
      }
    });

    return {
      rowId: id,
      columns,
    };
  });
};

export const getGradingConfigTypesFilter = () => {
  const types = [SCORE_BASED];

  return types;
};

export const getGradingConfigSettingValue = ({ settings, uid }) => {
  const setting = _.find(settings, { uid });
  return normalizeSettingValue(setting?.value, getIsJsonstringifyUid(uid));
};

export const checkAssignmentContributionMethodValue = params => {
  const {
    learningCourseId,
    subjectIds,
    primaryGradeId,
    curriculumProgramType,
    academicYearId,
    methods = [CUSTOM_WEIGHTS],
  } = params;

  const isCourseBasedGrading = getIsCourseBasedGradingEnabled();

  if (isCourseBasedGrading) {
    const course = getCourseGradingConfigQueryFromCache({
      learningCourseId,
      curriculumProgramType,
    });

    const hasScoreBasedMethodology = _.includes(
      _.get(course, "gradingMethodology.gradingMethodologyTypes", []),
      SCORE_BASED
    );

    const scoreGradingConfig = hasScoreBasedMethodology
      ? _.find(_.get(course, "gradingConfigurations"), {
          type: SCORE_BASED,
        })
      : {};

    const settings = _.get(scoreGradingConfig, "settings", []);
    const assignmentContributionMethod = _.find(settings, {
      uid: "assignmentContributionMethod",
    })?.value;

    return _.includes(methods, assignmentContributionMethod);
  } else {
    const subjects = getMultiSubjectsGradingConfigQueryFromCache({
      subjectIds,
      primaryGradeId,
      academicYearId,
    });

    // Subject must have SCORE_BASED in gradingMethodologyTypes AND
    // the SCORE_BASED gradingConfiguration must have assignmentContributionMethod in methods
    return _.some(subjects, subjectItem => {
      const gradingMethodologies = _.get(
        subjectItem,
        "gradingMethodologies",
        []
      );

      // Check methodology types include SCORE_BASED for this subject-grade pair
      const hasScoreBasedMethodology = _.some(gradingMethodologies, gm =>
        _.includes(_.get(gm, "gradingMethodologyTypes", []), SCORE_BASED)
      );

      if (!hasScoreBasedMethodology) return false;

      const gradingConfigurations = _.get(
        subjectItem,
        "gradingConfigurations",
        []
      );

      // Check SCORE_BASED config contains the expected assignmentContributionMethod
      const hasExpectedContributionMethod = _.some(
        gradingConfigurations,
        ({ type, settings }) =>
          type === SCORE_BASED &&
          _.some(
            settings,
            ({ uid, value }) =>
              uid === "assignmentContributionMethod" &&
              _.includes(methods, value)
          )
      );

      return hasExpectedContributionMethod;
    });
  }
};

export const convertMypGradeScaleToCriteriaSet = ({
  gradeScale,
  curriculumProgramId,
}) => {
  return {
    id: _.get(gradeScale, "id", ""),
    label: _.get(gradeScale, "displayName", ""),
    criteriaType: "GRADE_SCALE",
    grades: [],
    subType: null,
    isArchived: false,
    curriculumProgram: {
      id: curriculumProgramId,
    },
    academicCriteriaValues: _.map(
      _.get(gradeScale, "gradeScaleEntityDetails.0.gradeScaleValues", []),
      ({ id, abbreviation, color, description }) => {
        return {
          id,
          label: abbreviation, // for grade scale abbreviation is shown instead of label
          abbreviation,
          description,
          color,
          localGrades: [],
          scoreValues: [],
        };
      }
    ),
  };
};

const getIsPastGradingPeriod = term => {
  const endDate = _.get(term, "endDate");

  if (!endDate) {
    return false;
  }

  const termEndDate = moment(endDate);
  const currentDate = moment();

  return termEndDate.isBefore(currentDate, "day");
};

export const getDefaultVisibilityForType = type => {
  switch (type) {
    case SCORE:
    case GRADE_SCALE:
    case "GRADE_SCALES": {
      return VISIBLE_FOR_PAST_GRADING_PERIODS;
    }
    case "LIST":
    case "LEARNING_GOAL_LIST": {
      return "ALL";
    }
    case "LEARNING_GOAL_GRADE_SCALES":
    case "STANDARDS_GRADE_SCALES": {
      return ALWAYS_VISIBLE;
    }
    case "STANDARD_LIST": {
      return "ALL_STANDARDS";
    }
    case "STANDARDS_PROFICIENCY_SCALE": {
      return ALWAYS_VISIBLE;
    }
    case "CREDITS":
    case "GPA":
    case "GRADE_POINT":
    case "QUALITY_POINTS":
    case "GRADE_POINT_AVERAGE":
    case "CREDITS_TOTAL":
    case "CREDITS_AVERAGE":
    case "QUALITY_POINTS_TOTAL":
    case "AGGREGATE_SCORE":
    case "AGGREGATE_GRADE_SCALE":
    case "AGGREGATE_GRADE_SCALES": {
      return HIDDEN;
    }
    default: {
      return VISIBLE_FOR_PAST_GRADING_PERIODS;
    }
  }
};

export const getIsRatingVisible = ({
  term,
  userType,
  ratingType,
  gradeScaleId,
  visibilitySettings,
  isVisibilitySettingsV2Enabled,
}) => {
  if (!isVisibilitySettingsV2Enabled || userType === STAFF) return true;

  const defaultValue = getDefaultVisibilityForType(ratingType);
  let settingValue = defaultValue;

  switch (ratingType) {
    case SCORE:
      {
        settingValue = _.get(visibilitySettings, SCORE, defaultValue);
      }
      break;
    case CATEGORY_GRADE_SCALE:
    case GRADE_SCALE:
    case "LEARNING_GOAL_GRADE_SCALES":
    case "STANDARDS_GRADE_SCALES":
      {
        settingValue = _.get(
          visibilitySettings,
          ["GRADE_SCALES", gradeScaleId],
          defaultValue
        );
      }
      break;
    case "AGGREGATE_SCORE":
      {
        settingValue = _.get(
          visibilitySettings,
          ["AGGREGATES", "SCORE"],
          defaultValue
        );
      }
      break;
    case "AGGREGATE_GRADE_SCALE":
      {
        settingValue = _.get(
          visibilitySettings,
          ["AGGREGATES", "GRADE_SCALES", gradeScaleId],
          defaultValue
        );
      }
      break;
    case "STANDARDS_PROFICIENCY_SCALE": {
      settingValue = _.get(
        visibilitySettings,
        "STANDARDS_PROFICIENCY_SCALE",
        defaultValue
      );
      break;
    }
    case "GRADE_POINT": {
      settingValue = _.get(visibilitySettings, "GRADE_POINTS", defaultValue);
      break;
    }
    case "CREDITS": {
      settingValue = _.get(visibilitySettings, "COURSE_CREDITS", defaultValue);
      break;
    }
    case "QUALITY_POINTS": {
      settingValue = _.get(visibilitySettings, "QUALITY_POINTS", defaultValue);
      break;
    }
    case "GRADE_POINT_AVERAGE": {
      settingValue = _.get(
        visibilitySettings,
        "GRADE_POINT_AVERAGE",
        defaultValue
      );
      break;
    }
    case "CREDITS_TOTAL": {
      settingValue = _.get(visibilitySettings, "CREDITS_TOTAL", defaultValue);
      break;
    }
    case "CREDITS_AVERAGE": {
      settingValue = _.get(visibilitySettings, "CREDITS_AVERAGE", defaultValue);
      break;
    }
    case "QUALITY_POINTS_TOTAL": {
      settingValue = _.get(
        visibilitySettings,
        "QUALITY_POINTS_TOTAL",
        defaultValue
      );
      break;
    }
  }

  switch (settingValue) {
    case ALWAYS_VISIBLE: {
      return true;
    }
    case HIDDEN: {
      return false;
    }
    case VISIBLE_FOR_PAST_GRADING_PERIODS: {
      return getIsPastGradingPeriod(term);
    }
  }
};

const getUpdatedGradeScaleValues = ({
  gradeScaleValues,
  gradeScaleValuesLocal,
  usePresetScoreId = false,
}) => {
  const updateGradeScaleValues = [];

  _.forEach(gradeScaleValuesLocal, row => {
    const { id, gradeScaleValueScores, gradeScaleValueGradePoints } = row || {};
    const gradeScaleValueDataFromQuery = _.find(gradeScaleValues, { id });
    const {
      gradeScaleValueScores: gradeScaleValueScoresFromQuery,
      gradeScaleValueGradePoints: gradeScaleValueGradePointsFromQuery,
    } = gradeScaleValueDataFromQuery || {};

    const addGradeScaleValueScores = [];
    const updateGradeScaleValueScores = [];

    _.forEach(
      gradeScaleValueScores,
      ({
        id,
        minValue,
        maxValue,
        gradeValue,
        subjectLevel,
        presetGradeScaleValueScore,
      }) => {
        const gradeScaleScoreDataFromQuery = _.find(
          gradeScaleValueScoresFromQuery,
          { id }
        );

        const {
          minValue: minValueFromQuery,
          maxValue: maxValueFromQuery,
          gradeValue: gradeValueFromQuery,
        } = gradeScaleScoreDataFromQuery || {};

        if (
          minValue !== minValueFromQuery ||
          maxValue !== maxValueFromQuery ||
          gradeValue !== gradeValueFromQuery
        ) {
          const valueScoresObj = {
            id: usePresetScoreId ? presetGradeScaleValueScore?.id : id,
            minValue: parseFloat(minValue) || 0,
            maxValue: parseFloat(maxValue) || 0,
            gradeValue: parseFloat(gradeValue) || 0,
          };

          if (id) {
            updateGradeScaleValueScores.push(valueScoresObj);
          } else {
            addGradeScaleValueScores.push({
              ...valueScoresObj,
              subjectLevelId: subjectLevel?.id || null,
            });
          }
        }
      }
    );

    const newGradePointItem = _.first(gradeScaleValueGradePoints);
    const oldGradePointItem = _.first(gradeScaleValueGradePointsFromQuery);
    const hasGradePointChanged =
      !_.isNil(newGradePointItem) &&
      (_.get(newGradePointItem, "gradePoint") !==
        _.get(oldGradePointItem, "gradePoint") ||
        _.get(newGradePointItem, "isAddedValueApplicable") !==
          _.get(oldGradePointItem, "isAddedValueApplicable"));

    if (
      !_.isEmpty(updateGradeScaleValueScores) ||
      !_.isEmpty(addGradeScaleValueScores) ||
      hasGradePointChanged
    ) {
      const updateObj = {
        id,
        addGradeScaleValueScores,
        updateGradeScaleValueScores,
      };

      if (hasGradePointChanged) {
        const rawGradePoint = _.get(newGradePointItem, "gradePoint");
        const gradePointValue = _.toNumber(rawGradePoint);
        const upsertGradeScaleValueGradePoints = {
          isAddedValueApplicable:
            _.get(newGradePointItem, "isAddedValueApplicable") || false,
        };

        if (rawGradePoint !== "" && _.isFinite(gradePointValue)) {
          upsertGradeScaleValueGradePoints.gradePoint = gradePointValue;
        }

        updateObj.upsertGradeScaleValueGradePoints = upsertGradeScaleValueGradePoints;
      }

      updateGradeScaleValues.push(updateObj);
    }
  });

  return updateGradeScaleValues;
};

export const getUpdateGradeScaleEntityDetailsByType = ({
  subjectId,
  primaryGradeId,
  gradingPeriodId,
  learningCourseId,
  isCurriculumLevel,
  academicSetupType,
  curriculumProgramId,
  isCategoryGradeScale,
  gradingCategoryValueId,
}) => {
  if (isCategoryGradeScale) {
    return {
      entityType: "GRADING_CATEGORY_VALUE",
      entityDetail: {
        gradingCategoryValueId,
      },
    };
  }

  if (isCurriculumLevel) {
    return {
      entityType: "CURRICULUM_PROGRAM",
      entityDetail: {
        curriculumProgramId,
      },
    };
  }

  switch (academicSetupType) {
    case COURSE_BASED_GRADING: {
      return {
        entityType: "LEARNING_COURSE",
        entityDetail: {
          learningCourseId,
          academicTermIds: gradingPeriodId,
        },
      };
    }
    default: {
      return {
        entityType: "SUBJECT_GRADE_MAP",
        entityDetail: {
          subjectId,
          gradeId: primaryGradeId,
          academicTermIds: gradingPeriodId,
        },
      };
    }
  }
};

export const getShouldHideDefaultLevelInGradeScale = ({
  levels,
  curriculumType,
  differentGradebookValuesForLevels,
}) => {
  return (
    curriculumType === CURRICULUM_TYPE_DP &&
    !_.isEmpty(levels) &&
    differentGradebookValuesForLevels
  );
};

const getGradeValueCalculateTypeDetailDiff = ({
  gradeValueCalculateTypeDetail = [],
  gradeValueMethodLocal = [],
  differentGradebookValuesForLevels = false,
}) => {
  const add = [];
  const update = [];

  const detailMap = _.keyBy(gradeValueCalculateTypeDetail, d =>
    _.get(d, "subjectLevel.id", null)
  );

  if (differentGradebookValuesForLevels) {
    _.forEach(gradeValueMethodLocal, localItem => {
      const subjectLevelId = _.get(localItem, "subjectLevel.id", null);
      const localType = localItem.gradeValueCalculateType;
      const detail = detailMap[subjectLevelId];

      if (detail) {
        if (detail.gradeValueCalculateType !== localType) {
          update.push({
            id: detail.id,
            gradeValueCalculateType: localType,
          });
        }
      } else {
        add.push({
          gradeValueCalculateType: localType,
          subjectLevelId: subjectLevelId,
        });
      }
    });
  } else {
    const detail = detailMap[null];
    if (detail) {
      if (detail.gradeValueCalculateType !== gradeValueMethodLocal) {
        update.push({
          id: detail.id,
          gradeValueCalculateType: gradeValueMethodLocal,
        });
      }
    } else {
      add.push({
        gradeValueCalculateType: gradeValueMethodLocal,
        subjectLevelId: null,
      });
    }
  }

  return { add, update };
};

export const getUpdateGradeScaleInput = ({
  gradeScale,
  subjectId,
  primaryGradeId,
  academicSetupType,
  learningCourseId,
  curriculumProgramId,
  gradingPeriodId,
  gradeScaleValues,
  gradeScaleValuesLocal,
  gradeValueMethodLocal,
  differentGradebookValuesForLevels,
  gradeValueCalculateTypeDetail,
  isCurriculumLevel = false,
  isCategoryGradeScale,
  gradingCategoryValueId,
  usePresetScoreId = false,
}) => {
  const updateGradeScaleValues = getUpdatedGradeScaleValues({
    gradeScaleValues,
    usePresetScoreId,
    gradeScaleValuesLocal,
  });

  const {
    add: addGradeValueCalculateTypeDetail,
    update: updateGradeValueCalculateTypeDetail,
  } = getGradeValueCalculateTypeDetailDiff({
    gradeValueCalculateTypeDetail,
    gradeValueMethodLocal,
    differentGradebookValuesForLevels,
  });

  if (
    _.isEmpty(updateGradeScaleValues) &&
    _.isEmpty(updateGradeValueCalculateTypeDetail) &&
    _.isEmpty(addGradeValueCalculateTypeDetail)
  ) {
    return { shouldSkipMutation: true };
  }

  const { entityType, entityDetail } = getUpdateGradeScaleEntityDetailsByType({
    subjectId,
    primaryGradeId,
    gradingPeriodId,
    learningCourseId,
    isCurriculumLevel,
    academicSetupType,
    curriculumProgramId,
    isCategoryGradeScale,
    gradingCategoryValueId,
  });

  const updateGradeScaleInput = {
    id: _.get(gradeScale, "id"),
    entityType,
    entityDetail,
  };

  if (!_.isEmpty(updateGradeScaleValues)) {
    updateGradeScaleInput.updateGradeScaleValues = updateGradeScaleValues;
  }
  if (!_.isEmpty(updateGradeValueCalculateTypeDetail)) {
    updateGradeScaleInput.updateGradeValueCalculateTypeDetail = updateGradeValueCalculateTypeDetail;
  }
  if (!_.isEmpty(addGradeValueCalculateTypeDetail)) {
    updateGradeScaleInput.addGradeValueCalculateTypeDetail = addGradeValueCalculateTypeDetail;
  }

  return {
    input: updateGradeScaleInput,
  };
};

export const getSetIdsAndPlannerElementSetIdsMapFromState = state => {
  const datasetsGroupType = getCurrentDatasetsGroupType();

  const currentCurriculumProgramType = getCurrentCurriculumProgramType(state);
  const plannerElementSets = _.get(
    state,
    "platform.currentCurriculumProgram.plannerElementSets"
  );
  const currentPlannerElementSets = getCurrentPlannerElementSets(state);

  const datasetGroups =
    CURRICULUM_PROGRAM_TO_DATASET_GROUPS_MAP[currentCurriculumProgramType];
  const datasetTypes = datasetGroups?.[datasetsGroupType];
  const { setIds, setIdPlannerElementSetMap } = _.reduce(
    datasetTypes,
    (acc, type) => {
      const plannerElementSet =
        currentPlannerElementSets?.[type] ||
        _.find(plannerElementSets, { type });
      if (!plannerElementSet) return acc;
      const { id } = plannerElementSet;
      acc.setIds.push(id);
      acc.setIdPlannerElementSetMap[id] = plannerElementSet;
      return acc;
    },
    {
      setIds: [],
      setIdPlannerElementSetMap: {},
    }
  );
  return {
    setIds,
    setIdPlannerElementSetMap,
  };
};
